import { Button } from "../../ui/components/Button";
import { Dialog, DialogTrigger } from "../../ui/components/Dialog";
import { Tooltip, TooltipTrigger } from "../../ui/components/Tooltip";
import { ChevronDown, ChevronRight, Info, X } from "lucide-react";
import { Modal } from "react-aria-components";
import type { EditorSectionProps } from "../types";

export function EditorSection({
  title,
  description,
  isCollapsed,
  onToggleCollapse,
  disabled = false,
  children,
}: EditorSectionProps) {
  return (
    <div className={`border-border border-b ${disabled ? "opacity-50" : ""}`}>
      {/* Section Header */}
      <div className="flex flex-wrap items-center gap-2 p-4">
        <Button
          variant="ghost"
          onPress={onToggleCollapse}
          isDisabled={disabled}
          className="h-auto min-w-0 flex-1 justify-start p-0"
        >
          <div className="flex min-w-0 items-center gap-2">
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4 flex-shrink-0" />
            ) : (
              <ChevronDown className="h-4 w-4 flex-shrink-0" />
            )}
            <span className="truncate font-thin text-sm uppercase tracking-wide">{title}</span>
          </div>
        </Button>

        {/* Info Icon - Desktop: Tooltip on hover, Mobile: Dialog on tap */}

        {/* Desktop: Tooltip */}
        <TooltipTrigger delay={300}>
          <Button
            variant="ghost"
            size="sm"
            isDisabled={disabled}
            className="hidden md:flex h-auto flex-shrink-0 p-1"
          >
            <Info className="h-4 w-4 text-muted-foreground transition-colors hover:text-foreground" />
          </Button>
          <Tooltip placement="left">
            <div className="max-w-xs">
              <p className="text-sm">{description}</p>
            </div>
          </Tooltip>
        </TooltipTrigger>

        {/* Mobile: Dialog */}
        <DialogTrigger>
          <Button
            variant="ghost"
            size="sm"
            isDisabled={disabled}
            className="md:hidden h-auto flex-shrink-0 p-1"
          >
            <Info className="h-4 w-4 text-muted-foreground transition-colors hover:text-foreground" />
          </Button>
          <Modal className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <Dialog className="rounded-lg bg-background p-6 shadow-lg max-w-md w-full mx-4">
              {({ close }) => (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-lg">{title}</h4>
                    <Button variant="ghost" size="sm" onPress={close} className="h-6 w-6 p-0">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">{description}</p>
                </div>
              )}
            </Dialog>
          </Modal>
        </DialogTrigger>
      </div>

      {/* Section Content */}
      {!isCollapsed && (
        <div className="px-4 pb-4">
          <div className={disabled ? "pointer-events-none" : ""}>{children}</div>
        </div>
      )}
    </div>
  );
}
